/**
 * Per-device layout preferences for the mailbox — the resizable column widths
 * and the set of accounts collapsed in the unified ("All accounts") sidebar.
 * Like `useEmailActiveAccount` / `useEmailListPrefs`, these are UI state rather
 * than account data, so they live in localStorage under the `fias.email.*`
 * namespace. There is only ever one mounted mailbox, so — like
 * `useEmailActiveAccount` — no same-tab sync event is needed; a fresh mount
 * reads the last stored value.
 */

import { useState, useEffect, useCallback } from 'react';

// ── Resizable column widths (desktop 3-pane layout) ────────────────────────
// Defaults mirror the previous fixed Tailwind widths (w-56 / w-96) so the
// initial render is unchanged for anyone who never drags a divider.
export interface EmailColumnWidths {
  /** Accounts + folders sidebar width, px. */
  sidebar: number;
  /** Message-list (email summary) column width, px. */
  list: number;
}

export const DEFAULT_EMAIL_COLUMN_WIDTHS: EmailColumnWidths = { sidebar: 224, list: 384 };

/** Clamp bounds so a divider can never hide a column or crowd the reading pane. */
export const EMAIL_COLUMN_BOUNDS = {
  sidebar: { min: 160, max: 420 },
  list: { min: 280, max: 640 },
} as const;

const WIDTHS_KEY = 'fias.email.columnWidths';

function clampWidths(w: EmailColumnWidths): EmailColumnWidths {
  const c = (n: number, b: { min: number; max: number }) =>
    Math.min(b.max, Math.max(b.min, Math.round(n)));
  return {
    sidebar: c(w.sidebar, EMAIL_COLUMN_BOUNDS.sidebar),
    list: c(w.list, EMAIL_COLUMN_BOUNDS.list),
  };
}

function loadColumnWidths(): EmailColumnWidths {
  try {
    const raw = localStorage.getItem(WIDTHS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<EmailColumnWidths>;
      return clampWidths({
        sidebar: Number(parsed.sidebar) || DEFAULT_EMAIL_COLUMN_WIDTHS.sidebar,
        list: Number(parsed.list) || DEFAULT_EMAIL_COLUMN_WIDTHS.list,
      });
    }
  } catch {
    // ignore malformed / unavailable storage
  }
  return DEFAULT_EMAIL_COLUMN_WIDTHS;
}

/** Read + update the persisted column widths (values are clamped to bounds). */
export function useEmailColumnWidths(): [
  EmailColumnWidths,
  (next: Partial<EmailColumnWidths>) => void,
] {
  const [widths, setWidths] = useState<EmailColumnWidths>(() => loadColumnWidths());

  useEffect(() => {
    try {
      localStorage.setItem(WIDTHS_KEY, JSON.stringify(widths));
    } catch {
      // ignore unavailable storage
    }
  }, [widths]);

  const update = useCallback((next: Partial<EmailColumnWidths>) => {
    setWidths((prev) => clampWidths({ ...prev, ...next }));
  }, []);

  return [widths, update];
}

// ── Collapsed accounts (unified sidebar) ───────────────────────────────────
const COLLAPSED_KEY = 'fias.email.collapsedAccounts';

function loadCollapsedAccounts(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as unknown;
      if (Array.isArray(arr)) return new Set(arr.filter((v): v is string => typeof v === 'string'));
    }
  } catch {
    // ignore malformed / unavailable storage
  }
  return new Set();
}

export interface CollapsedAccountsControl {
  isCollapsed: (accountId: string) => boolean;
  toggle: (accountId: string) => void;
}

/**
 * Tracks which accounts are collapsed (folders hidden) in the unified sidebar,
 * so a user with many mailboxes can minimize the ones they aren't focused on.
 * Persisted per-device.
 */
export function useCollapsedEmailAccounts(): CollapsedAccountsControl {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsedAccounts());

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed]));
    } catch {
      // ignore unavailable storage
    }
  }, [collapsed]);

  const isCollapsed = useCallback((accountId: string) => collapsed.has(accountId), [collapsed]);
  const toggle = useCallback((accountId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  }, []);

  return { isCollapsed, toggle };
}

// ── Collapsed folders (nested-folder sidebar tree) ─────────────────────────
const COLLAPSED_FOLDERS_KEY = 'fias.email.collapsedFolders';

function loadCollapsedFolders(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_FOLDERS_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as unknown;
      if (Array.isArray(arr)) return new Set(arr.filter((v): v is string => typeof v === 'string'));
    }
  } catch {
    // ignore malformed / unavailable storage
  }
  return new Set();
}

export interface CollapsedFoldersControl {
  isCollapsed: (folderId: string) => boolean;
  toggle: (folderId: string) => void;
  /** Force a folder open (used when creating a subfolder under it). */
  expand: (folderId: string) => void;
}

/**
 * Tracks which parent folders are collapsed (their subtree hidden) in the
 * sidebar tree. We persist the COLLAPSED set so folders default to expanded — a
 * newly-created subfolder is visible without a click. Persisted per-device,
 * mirroring `useCollapsedEmailAccounts`.
 */
export function useCollapsedEmailFolders(): CollapsedFoldersControl {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsedFolders());

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_FOLDERS_KEY, JSON.stringify([...collapsed]));
    } catch {
      // ignore unavailable storage
    }
  }, [collapsed]);

  const isCollapsed = useCallback((folderId: string) => collapsed.has(folderId), [collapsed]);
  const toggle = useCallback((folderId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);
  const expand = useCallback((folderId: string) => {
    setCollapsed((prev) => {
      if (!prev.has(folderId)) return prev;
      const next = new Set(prev);
      next.delete(folderId);
      return next;
    });
  }, []);

  return { isCollapsed, toggle, expand };
}

// ── Search-bar mode (filter vs search) ─────────────────────────────────────
// 'filter' narrows the CURRENT view in memory (instant); 'search' runs a
// server-side lookup across ALL accounts + folders (Apple-style). Persisted so
// the chosen behavior sticks. Defaults to 'filter' — the lighter everyday case.
export type EmailSearchMode = 'filter' | 'search';

const SEARCH_MODE_KEY = 'fias.email.searchMode';

function loadSearchMode(): EmailSearchMode {
  try {
    return localStorage.getItem(SEARCH_MODE_KEY) === 'search' ? 'search' : 'filter';
  } catch {
    return 'filter';
  }
}

export function useEmailSearchMode(): [EmailSearchMode, (next: EmailSearchMode) => void] {
  const [mode, setMode] = useState<EmailSearchMode>(() => loadSearchMode());

  useEffect(() => {
    try {
      localStorage.setItem(SEARCH_MODE_KEY, mode);
    } catch {
      // ignore unavailable storage
    }
  }, [mode]);

  return [mode, setMode];
}

// ── Sidebar "Tools" section collapse ───────────────────────────────────────
// The feature-tool group (Scheduled/Snoozed/Contacts/Filters/Screener/Spam &
// AI Filter/Settings) collapses to declutter the sidebar. Persisted per-device.
const TOOLS_COLLAPSED_KEY = 'fias.email.toolsCollapsed';

function loadToolsCollapsed(): boolean {
  try {
    return localStorage.getItem(TOOLS_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function useEmailToolsCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => loadToolsCollapsed());
  const toggle = () =>
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(TOOLS_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // ignore unavailable storage
      }
      return next;
    });
  return [collapsed, toggle];
}
